<script setup lang="ts">
import './assets/style.scss'
import { VFilePickerField, VIcons } from '@/shared/ui'
import { computed } from 'vue'
import { useUploadObjectFiles } from './../model/useUploadObjectFiles.ts'
import type { InventoryMapInfoType } from '@/entities/Inventory/Main'
import { InventObjectFileRemove } from '@/features/Inventory/ObjectFileRemove'
import { useHelpers } from '@/shared/lib'
import { useConfig } from './../config'
import { useAuth } from '@/entities/Auth'

const props = defineProps<{
  labelKey?: string
  /** Create V2 batch: keep picked files on the model until the object has a server id. */
  draftMode?: boolean
}>()

const object = defineModel<InventoryMapInfoType>({ required: true })

const { formatFileSize } = useHelpers()

const {
  uploadedFiles,
  rules,
  isLoading,
  photoLimit,
  fileLimit,
  isExtImage,
  openHandler,
  downloadHandler,
} = useUploadObjectFiles(
  () => object.value.id,
  (files) => {
    object.value.files = [...(object.value.files ?? []), ...(files as InventoryMapInfoType['files'])]
  },
  {
    draftMode: props.draftMode,
    onDraftFilesSelected: (files) => {
      const next = [...(object.value.files ?? [])]
      for (const file of files) {
        const splitName = file.name.split('.')
        const ext = splitName.pop() || ''
        next.push({
          file: URL.createObjectURL(file),
          filename: splitName.join('.'),
          ext,
          size: formatFileSize(file.size),
        })
      }
      object.value.files = next
    },
  },
)
const { openAddress } = useConfig()
const { isPermissions } = useAuth()

const canEdit = computed(() => isPermissions(['OBJECT_EDIT']))

const filesInfo = computed<{ count: number; size: string }>(() => {
  const count = uploadedFiles.value.length
  const size = uploadedFiles.value.reduce((sum: number, item: File) => sum + item.size, 0)

  return { count, size: formatFileSize(size) }
})

const images = computed(() =>
  (object.value.files || []).filter((item) => isExtImage(item.ext)).map((item) => item.file),
)

const removedHandler = (file: string) => {
  object.value.files = (object.value.files ?? []).filter((item) => item.file !== file)
}
</script>

<template>
  <div class="file-loader">
    <div v-if="images.length" class="file-loader__images mb-6">
      <VCarousel height="auto" hide-delimiters>
        <template v-slot:prev="{ props }">
          <VBtn
            @click="props.onClick"
            color="grey"
            variant="outlined"
            class="file-loader__arrow"
            icon="chevron_left"
          />
        </template>

        <template v-slot:next="{ props }">
          <VBtn
            @click="props.onClick"
            color="grey"
            variant="outlined"
            class="file-loader__arrow"
            icon="chevron_right"
          />
        </template>

        <VCarouselItem v-for="img in images" :key="img">
          <img :src="img.startsWith('blob:') ? img : openAddress + img" alt="preview" />
        </VCarouselItem>
      </VCarousel>
    </div>

    <div class="file-loader__label">
      {{ props.labelKey ? $t(props.labelKey) : $t('itf.added_files') }}

      <VTooltip
        :text="$t('itf.file_limits', { photo_limit: photoLimit, file_limit: fileLimit })"
        location="top"
      >
        <template #activator="{ props }">
          <VIcons type="info" v-bind="props" />
        </template>
      </VTooltip>
    </div>

    <div v-if="(object.files || []).length" class="file-loader__list mb-6">
      <div v-for="file in object.files" :key="file.filename" class="file-loader__item">
        <VIcons class="file-loader__item-icon" :type="isExtImage(file.ext) ? 'image' : 'article'" />

        <div class="file-loader__item-name">
          <p>{{ file.filename }}</p>
          <span>{{ file.size }}</span>
        </div>

        <div class="file-loader__options">
          <VSpeedDial location="left center" transition="scale-transition">
            <template v-slot:activator="{ props }">
              <VBtn color="default" v-bind="props" icon density="compact">
                <VIcons type="more_vert" />
              </VBtn>
            </template>

            <InventObjectFileRemove
              key="1"
              v-if="canEdit && object.id"
              :file="file.file"
              :object-id="object.id"
              :removed-handler="() => removedHandler(file.file)"
            />

            <VTooltip v-else-if="canEdit && draftMode" location="top" :text="$t('itf.delete')">
              <template #activator="{ props: tipProps }">
                <VFab
                  key="1-draft"
                  color="error"
                  icon
                  density="compact"
                  v-bind="tipProps"
                  @click="removedHandler(file.file)"
                >
                  <VIcons type="delete" />
                </VFab>
              </template>
            </VTooltip>

            <VFab
              key="2"
              color="primary"
              icon
              density="compact"
              @click="downloadHandler(file.file)"
            >
              <VIcons type="download" />
            </VFab>

            <VTooltip location="top" :text="$t('itf.view')">
              <template #activator="{ props }">
                <VFab
                  key="3"
                  color="primary"
                  icon
                  density="compact"
                  v-bind="props"
                  @click="openHandler(file.file)"
                >
                  <VIcons type="visibility" />
                </VFab>
              </template>
            </VTooltip>
          </VSpeedDial>
        </div>
      </div>
    </div>

    <div :class="{ disabled: !canEdit }">
      <VFilePickerField
        v-model="uploadedFiles"
        :label="$t('itf.attach_file')"
        :rules="rules.files"
        :loading="isLoading"
        :disabled="!canEdit"
        multiple
      >
        <template #selection>
          {{ $t('itf.file_selection', { ...filesInfo }) }}
        </template>
      </VFilePickerField>
    </div>
  </div>
</template>
